import React, { useContext, useState } from "react";
import styled from "styled-components";
import { db, storage } from "../firebase/firebaseConfig";
import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserContext } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function ProjectForm() {
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [project, setProject] = useState({
    title: "",
    liveLink: "",
    github: "",
    description: "",
    technologies: "",
    role: "",
    start: "",
    end: "",
    image: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setProject((prev) => ({ ...prev, image: files[0] }));
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setProject((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      toast.error("Please log in first!");
      return;
    }

    setLoading(true);
    try {
      let imageUrl = "";

      if (project.image) {
        const imageRef = ref(
          storage,
          `users/${user.id}/projects/${Date.now()}_${project.image.name}`
        );
        await uploadBytes(imageRef, project.image);
        imageUrl = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, "users", user.id, "projects"), {
        ...project,
        imageUrl,
        createdAt: new Date(),
      });

      toast.success("🚀 Project added successfully!");
      navigate("/profile/" + user.id);
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("❌ Error saving project.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>Add New Project</Title>
        <Form onSubmit={handleSubmit}>
          {/* Title */}
          <FieldGroup>
            <Label>Project Title *</Label>
            <Input
              name="title"
              value={project.title}
              onChange={handleChange}
              required
            />
          </FieldGroup>

          {/* Links */}
          <Row>
            <FieldGroup>
              <Label>Live Demo</Label>
              <Input
                name="liveLink"
                value={project.liveLink}
                onChange={handleChange}
                placeholder="https://"
              />
            </FieldGroup>
            <FieldGroup>
              <Label>GitHub Repo</Label>
              <Input
                name="github"
                value={project.github}
                onChange={handleChange}
                placeholder="https://github.com/..."
              />
            </FieldGroup>
          </Row>

          {/* Description */}
          <FieldGroup>
            <Label>Description</Label>
            <TextArea
              name="description"
              rows="4"
              value={project.description}
              onChange={handleChange}
              required
            />
          </FieldGroup>

          {/* Tech + Role */}
          <Row>
            <FieldGroup>
              <Label>Technologies Used</Label>
              <Input
                name="technologies"
                value={project.technologies}
                onChange={handleChange}
                placeholder="React, Node, Firebase..."
                required
              />
            </FieldGroup>
            <FieldGroup>
              <Label>Your Role</Label>
              <Input
                name="role"
                value={project.role}
                onChange={handleChange}
                placeholder="Frontend Developer"
                required
              />
            </FieldGroup>
          </Row>

          {/* Timeline */}
          <Row>
            <FieldGroup>
              <Label>Start Date</Label>
              <Input
                type="date"
                name="start"
                value={project.start}
                onChange={handleChange}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>End Date</Label>
              <Input
                type="date"
                name="end"
                value={project.end}
                onChange={handleChange}
              />
            </FieldGroup>
          </Row>

          {/* Image Upload */}
          <FieldGroup>
            <Label>Project Thumbnail</Label>
            <FileInputWrapper>
              <FileInput
                type="file"
                name="image"
                accept="image/*"
                onChange={handleChange}
                required
              />
              <FileLabel>📁 Choose Image</FileLabel>
            </FileInputWrapper>
            {imagePreview && <ImagePreview src={imagePreview} alt="Preview" />}
          </FieldGroup>

          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Submit Project"}
          </Button>
        </Form>
      </Card>

      {loading && (
        <Overlay>
          <Loader />
        </Overlay>
      )}
    </Container>
  );
}

/* ---------- Styled Components ---------- */

const Container = styled.div`
  background: radial-gradient(circle at top left, #0b0617, #000);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 750px;
  color: #fff;
  box-shadow: 0 0 40px rgba(155, 92, 255, 0.2);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 26px;
  margin-bottom: 30px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0; /* Fix overflow shrinking */
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-size: 14px;
  color: #bbb;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border: 1px solid rgba(155, 92, 255, 0.5);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  resize: vertical;
  box-sizing: border-box;
  min-height: 100px;

  &:focus {
    border: 1px solid rgba(155, 92, 255, 0.5);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
    outline: none;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  width: 100%;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const FileInput = styled.input`
  opacity: 0;
  width: 100%;
  position: absolute;
  height: 45px;
  cursor: pointer;
`;

const FileLabel = styled.div`
  background: rgba(155, 92, 255, 0.15);
  border: 1px dashed rgba(155, 92, 255, 0.4);
  color: #c59fff;
  padding: 12px;
  border-radius: 10px;
  text-align: center;
  transition: 0.3s;
  font-weight: 500;

  &:hover {
    background: rgba(155, 92, 255, 0.3);
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 12px;
  margin-top: 12px;
  border: 1px solid rgba(155, 92, 255, 0.3);
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Loader = styled.div`
  border: 6px solid rgba(255, 255, 255, 0.2);
  border-top: 6px solid #a855f7;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;
